import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { toast } from "sonner";

export interface UserRow {
  id: string;
  display_name: string | null;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  roles: string[];
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  banned: boolean;
}

function makeHook(action: string, successMsg: string) {
  return function () {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (body: Record<string, unknown>) => {
        const { data, error } = await supabase.functions.invoke(
          `manage-users?action=${action}`,
          { method: "POST", body }
        );
        // #719: FunctionsHttpError's message is a constant; the real one is on
        // error.context. Without this the role-grant failures are invisible.
        if (error) throw new Error(await edgeFunctionErrorMessage(error));
        if (data?.error) throw new Error(data.error);
        return data;
      },
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["users"] });
        toast.success(successMsg);
      },
      onError: (e: Error) => toast.error(e.message),
    });
  };
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("manage-users?action=list", {
        method: "GET",
      });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data.users as UserRow[];
    },
  });
}

/**
 * Each user's last in-app change (#1183), user id → ISO time, from the admin-only
 * get_users_last_active() RPC. A user with no audit-log rows is absent.
 */
export function useUsersLastActive() {
  return useQuery({
    queryKey: ["users", "last-active"],
    queryFn: async (): Promise<Map<string, string>> => {
      // The function ships in this PR's migration, so the generated types only
      // learn its name at the next types:sync after deploy.
      const { data, error } = await supabase.rpc("get_users_last_active" as never);
      if (error) throw error;
      const rows = (data ?? []) as unknown as { user_id: string; last_active_at: string }[];
      return new Map(rows.map((r) => [r.user_id, r.last_active_at]));
    },
  });
}

export const useInviteUser = makeHook("invite", "Invitation sent");
export const useUpdateUserRole = makeHook("update-role", "Role updated");
export const useResendInvite = makeHook("resend-invite", "Invite resent");
export const useDeleteUser = makeHook("delete-user", "User removed");
export const useResetPassword = makeHook("reset-password", "Password reset email sent");
export const useUpdateProfile = makeHook("update-profile", "Profile updated");
export const useBanUser = makeHook("ban-user", "User suspended");
export const useUnbanUser = makeHook("unban-user", "User reactivated");
export const useSetPassword = makeHook("set-password", "Password updated");
export const useConfirmEmail = makeHook("confirm-email", "Email marked as verified");
